import { useEffect, useMemo, useRef, useState } from 'react';
import getSymbolFromCurrency from 'currency-symbol-map';
import useLineGraphData, { OVERVIEW_DEFAULT_PERIOD, OVERVIEW_DEFAULT_PERIODS, } from 'design-system/LineChart/useLineGraphData';
import { parse, getYExtremes } from 'design-system/LineChart/helpers';
import { selectSavingsBalance, selectCheckingBalance, selectNetWorthBalance, selectInvestmentBalance, selectSectionedAccounts, selectDebtBalanceHistory, selectAssetsBalance, selectALLBalanceHistory, } from 'reducers/selectors';
import { useAppSelector } from 'store/hooks';
import { ChartType, } from '../types';
import { useBalanceHistory } from './useBalanceHistory';
export const getPointsFromData = (dataArr, yExtremes, useAbsY, // this is used to turn negative graph plot as positive graph
useZeroAsFirstItem) => {
    if (dataArr?.length) {
        let isAllSame = true;
        let checkerNum;
        const data = dataArr.map((data, index) => {
            if (typeof checkerNum === 'number' && data.balance !== checkerNum) {
                isAllSame = false;
            }
            checkerNum = data.balance;
            return {
                ...data,
                x: index,
                y: data.balance,
                actualY: data.balance,
                currencySymbol: getSymbolFromCurrency(data.currency),
            };
        });
        const [parsedData, zero] = parse(data, isAllSame, yExtremes, useAbsY);
        let maxPoint = null;
        let minPoint = null;
        const lastItem = parsedData[parsedData.length - 1];
        const firstItem = 
        // eslint-disable-next-line no-nested-ternary
        isAllSame ? null : useZeroAsFirstItem ? zero : parsedData[0];
        if (!isAllSame) {
            parsedData.forEach((dataPoint) => {
                if (!minPoint || minPoint?.actualY > dataPoint?.actualY) {
                    minPoint = dataPoint;
                }
                if (!maxPoint || maxPoint?.actualY <= dataPoint?.actualY) {
                    maxPoint = dataPoint;
                }
            });
        }
        return { points: parsedData, minPoint, maxPoint, lastItem, firstItem };
    }
    return {
        points: [],
        minPoint: null,
        maxPoint: null,
        lastItem: null,
        firstItem: null,
    };
};
const defaultCache = () => ({
    '1W': null,
    '1M': null,
    '3M': null,
    '6M': null,
    YTD: null,
    '1Y': null,
    ALL: null,
});
const useOverviewChartCache = ({ checkingBalance, savingsBalance, investmentsBalance, networthBalance, assetsBalance, debtBalance, }) => {
    const cache = useRef({
        Everyday: defaultCache(),
        Savings: defaultCache(),
        Investments: defaultCache(),
        'Net Worth - Main': defaultCache(),
        'Net Worth - Main Asset': defaultCache(),
        'Net Worth - Main Debt': defaultCache(),
        'Net Worth - Asset': defaultCache(),
        'Net Worth - Debt': defaultCache(),
    });
    useEffect(() => {
        cache.current.Everyday = defaultCache();
    }, [checkingBalance]);
    useEffect(() => {
        cache.current.Savings = defaultCache();
    }, [savingsBalance]);
    useEffect(() => {
        cache.current.Investments = defaultCache();
    }, [investmentsBalance]);
    useEffect(() => {
        cache.current['Net Worth - Main'] = defaultCache();
    }, [networthBalance]);
    useEffect(() => {
        cache.current['Net Worth - Main Asset'] = defaultCache();
        cache.current['Net Worth - Asset'] = defaultCache();
    }, [assetsBalance]);
    useEffect(() => {
        cache.current['Net Worth - Main Debt'] = defaultCache();
        cache.current['Net Worth - Debt'] = defaultCache();
    }, [debtBalance]);
    return cache;
};
const useOverviewChart = (overviewScreenTitle) => {
    const checkingBalance = useAppSelector(selectCheckingBalance);
    const networthBalance = useAppSelector(selectNetWorthBalance);
    const savingsBalance = useAppSelector(selectSavingsBalance);
    const investmentsBalance = useAppSelector(selectInvestmentBalance);
    const assetsBalance = useAppSelector(selectAssetsBalance);
    const debtBalance = useAppSelector(selectDebtBalanceHistory);
    const [chartType, setChartType] = useState(ChartType.line);
    const pieChartData = useAppSelector((state) => selectSectionedAccounts(state, overviewScreenTitle, chartType === ChartType.pie));
    const allData = useAppSelector((state) => selectALLBalanceHistory(state, overviewScreenTitle, chartType === ChartType.assetVsDebt));
    useBalanceHistory(overviewScreenTitle);
    const cache = useOverviewChartCache({
        checkingBalance: checkingBalance?.lastUpdated[0]?.balance,
        savingsBalance: savingsBalance?.lastUpdated[0]?.balance,
        investmentsBalance: investmentsBalance?.lastUpdated[0]?.balance,
        networthBalance: networthBalance?.lastUpdated[0]?.balance,
        assetsBalance: assetsBalance?.lastUpdated[0]?.balance,
        debtBalance: debtBalance?.lastUpdated[0]?.balance,
    });
    // ALL is fetched from a separate endpoint and can arrive after the 1Y data
    // that resets the cache, so drop the cached ALL points when it changes
    useEffect(() => {
        Object.keys(cache.current).forEach((section) => {
            cache.current[section] = { ...cache.current[section], ALL: null };
        });
    }, [allData, cache]);
    const { portfolioDataSet, portfolioDataSet2, setPortfolioDataSet, setPortfolioDataSet2, selectedPeriod, setSelectedPeriod, requesting, setRequesting, selectedInfoObj, selectedInfoObj2, } = useLineGraphData(OVERVIEW_DEFAULT_PERIODS, OVERVIEW_DEFAULT_PERIOD);
    const selectedTitle = useRef(overviewScreenTitle);
    useEffect(() => {
        if (((overviewScreenTitle === 'Net Worth - Main' ||
            overviewScreenTitle === 'Everyday') &&
            chartType === ChartType.pie) ||
            (overviewScreenTitle !== 'Net Worth - Main' &&
                chartType === ChartType.assetVsDebt)) {
            setChartType(ChartType.line);
        }
    }, [chartType, overviewScreenTitle]);
    const pieData = useMemo(() => {
        if (chartType === ChartType.pie) {
            return pieChartData;
        }
        return [];
    }, [chartType, pieChartData]);
    useEffect(() => {
        if (chartType === ChartType.line || chartType === ChartType.assetVsDebt) {
            const isAssetsVsDebt = chartType === ChartType.assetVsDebt;
            if (!isAssetsVsDebt &&
                cache.current[overviewScreenTitle][selectedPeriod]) {
                // case where there is data in cache
                setRequesting(false);
                setPortfolioDataSet((oldDataSet) => ({
                    ...oldDataSet,
                    ...cache.current[overviewScreenTitle],
                }));
                setPortfolioDataSet2((oldDataSet) => ({
                    ...oldDataSet,
                    '1W': null,
                    '1M': null,
                    '3M': null,
                    '6M': null,
                    YTD: null,
                    '1Y': null,
                    ALL: null,
                }));
            }
            else if (isAssetsVsDebt &&
                cache.current['Net Worth - Main Asset'][selectedPeriod] &&
                cache.current['Net Worth - Main Debt'][selectedPeriod]) {
                // case where there is data in cache
                setRequesting(false);
                setPortfolioDataSet((oldDataSet) => ({
                    ...oldDataSet,
                    ...cache.current['Net Worth - Main Asset'],
                }));
                setPortfolioDataSet2((oldDataSet) => ({
                    ...oldDataSet,
                    ...cache.current['Net Worth - Main Debt'],
                }));
            }
            else {
                // case where there is no data in cache
                // eslint-disable-next-line no-lonely-if
                if (selectedPeriod === 'ALL') {
                    // case for 'ALL' is handled separately because we fetch 'ALL' from different api
                    if (isAssetsVsDebt && allData[0].length && allData[1]?.length) {
                        const YExtremes = getYExtremes(allData[0], allData[1], false, false);
                        const data1 = getPointsFromData(allData[0], YExtremes, undefined, true);
                        const data2 = getPointsFromData(allData[1], YExtremes, false);
                        const finalMaxPoint = 
                        // eslint-disable-next-line no-nested-ternary
                        data1.maxPoint && data2.maxPoint
                            ? data2.maxPoint.actualY > data1.maxPoint.actualY
                                ? data2.maxPoint
                                : data1.maxPoint
                            : null;
                        const finalMinPoint = 
                        // eslint-disable-next-line no-nested-ternary
                        data1.minPoint && data2.minPoint
                            ? data1.minPoint.actualY < data2.minPoint.actualY
                                ? data1.minPoint
                                : data2.minPoint
                            : null;
                        cache.current['Net Worth - Main Asset'][selectedPeriod] = {
                            points: data1.points,
                            maxPoint: finalMaxPoint,
                            minPoint: finalMinPoint,
                            lastItem: data1.lastItem,
                            firstItem: data1.firstItem,
                        };
                        cache.current['Net Worth - Main Debt'][selectedPeriod] = {
                            maxPoint: null,
                            minPoint: null,
                            firstItem: null,
                            points: data2?.points,
                            lastItem: data2?.lastItem,
                        };
                        setPortfolioDataSet((oldDataSet) => ({
                            ...(selectedTitle.current !== overviewScreenTitle
                                ? {
                                    '1W': null,
                                    '1M': null,
                                    '3M': null,
                                    '6M': null,
                                    YTD: null,
                                    '1Y': null,
                                    ALL: null,
                                }
                                : oldDataSet),
                            [selectedPeriod]: {
                                points: data1.points,
                                maxPoint: finalMaxPoint,
                                minPoint: finalMinPoint,
                                lastItem: data1.lastItem,
                                firstItem: data1.firstItem,
                            },
                        }));
                        setPortfolioDataSet2((oldDataSet) => ({
                            ...(selectedTitle.current !== overviewScreenTitle
                                ? {
                                    '1W': null,
                                    '1M': null,
                                    '3M': null,
                                    '6M': null,
                                    YTD: null,
                                    '1Y': null,
                                    ALL: null,
                                }
                                : oldDataSet),
                            [selectedPeriod]: {
                                maxPoint: null,
                                minPoint: null,
                                firstItem: null,
                                points: data2.points,
                                lastItem: data2.lastItem,
                            },
                        }));
                        setRequesting(false);
                    }
                    else if (allData[0].length) {
                        const data = getPointsFromData(allData[0]);
                        cache.current[overviewScreenTitle][selectedPeriod] = {
                            points: data.points,
                            maxPoint: data.maxPoint,
                            minPoint: data.minPoint,
                            lastItem: data.lastItem,
                            firstItem: data.firstItem,
                        };
                        setPortfolioDataSet((oldDataSet) => ({
                            ...(selectedTitle.current !== overviewScreenTitle
                                ? {
                                    '1W': null,
                                    '1M': null,
                                    '3M': null,
                                    '6M': null,
                                    YTD: null,
                                    '1Y': null,
                                    ALL: null,
                                }
                                : oldDataSet),
                            [selectedPeriod]: {
                                points: data.points,
                                maxPoint: data.maxPoint,
                                minPoint: data.minPoint,
                                lastItem: data.lastItem,
                                firstItem: data.firstItem,
                            },
                        }));
                        setRequesting(false);
                    }
                    else {
                        setRequesting(true);
                        setPortfolioDataSet((oldDataSet) => ({
                            ...oldDataSet,
                            '1W': null,
                            '1M': null,
                            '3M': null,
                            '6M': null,
                            YTD: null,
                            '1Y': null,
                            ALL: null,
                        }));
                        setPortfolioDataSet2((oldDataSet) => ({
                            ...oldDataSet,
                            '1W': null,
                            '1M': null,
                            '3M': null,
                            '6M': null,
                            YTD: null,
                            '1Y': null,
                            ALL: null,
                        }));
                    }
                }
                else {
                    // case for other periods besides ALL/MAX
                    const rawData = (() => {
                        switch (overviewScreenTitle) {
                            case 'Everyday': {
                                return [checkingBalance];
                            }
                            case 'Savings': {
                                return [savingsBalance];
                            }
                            case 'Investments': {
                                return [investmentsBalance];
                            }
                            case 'Net Worth - Main': {
                                if (isAssetsVsDebt) {
                                    if (assetsBalance && debtBalance) {
                                        return [assetsBalance, debtBalance];
                                    }
                                    return [];
                                }
                                return [networthBalance];
                            }
                            case 'Net Worth - Debt': {
                                return [debtBalance];
                            }
                            case 'Net Worth - Asset': {
                                return [assetsBalance];
                            }
                            default: {
                                return null;
                            }
                        }
                    })();
                    if (rawData?.[0]) {
                        const data = (() => {
                            switch (selectedPeriod) {
                                case '1W': {
                                    if (rawData?.[0].week?.length) {
                                        let YExtremes;
                                        if (isAssetsVsDebt &&
                                            rawData?.[0].week?.length &&
                                            rawData?.[1]?.week?.length) {
                                            YExtremes = getYExtremes(rawData[0].week, rawData[1].week, false, false);
                                        }
                                        return {
                                            one: getPointsFromData(rawData?.[0].week, YExtremes, false, isAssetsVsDebt),
                                            two: rawData?.[1]?.week
                                                ? getPointsFromData(rawData?.[1].week, YExtremes, false)
                                                : null,
                                        };
                                    }
                                    return null;
                                }
                                case '1M': {
                                    if (rawData?.[0].month?.length) {
                                        let YExtremes;
                                        if (isAssetsVsDebt &&
                                            rawData?.[0].month?.length &&
                                            rawData?.[1]?.month?.length) {
                                            YExtremes = getYExtremes(rawData[0].month, rawData[1].month, false, false);
                                        }
                                        return {
                                            one: getPointsFromData(rawData?.[0].month, YExtremes, false, isAssetsVsDebt),
                                            two: rawData?.[1]?.month
                                                ? getPointsFromData(rawData?.[1].month, YExtremes, false)
                                                : null,
                                        };
                                    }
                                    return null;
                                }
                                case 'YTD': {
                                    if (rawData?.[0].ytd?.length) {
                                        let YExtremes;
                                        if (isAssetsVsDebt &&
                                            rawData?.[0].ytd?.length &&
                                            rawData?.[1]?.ytd?.length) {
                                            YExtremes = getYExtremes(rawData[0].ytd, rawData[1].ytd, false, false);
                                        }
                                        return {
                                            one: getPointsFromData(rawData?.[0].ytd, YExtremes, false, isAssetsVsDebt),
                                            two: rawData?.[1]?.ytd
                                                ? getPointsFromData(rawData?.[1].ytd, YExtremes, false)
                                                : null,
                                        };
                                    }
                                    return null;
                                }
                                case '1Y': {
                                    let YExtremes;
                                    if (isAssetsVsDebt &&
                                        rawData?.[0].year?.length &&
                                        rawData?.[1]?.year?.length) {
                                        YExtremes = getYExtremes(rawData[0].year, rawData[1].year, false, false);
                                    }
                                    if (rawData?.[0].year?.length) {
                                        return {
                                            one: getPointsFromData(rawData?.[0].year, YExtremes, false, isAssetsVsDebt),
                                            two: rawData?.[1]?.year
                                                ? getPointsFromData(rawData?.[1].year, YExtremes, false)
                                                : null,
                                        };
                                    }
                                    return null;
                                }
                                case '6M': {
                                    if (rawData?.[0].sixmonths?.length) {
                                        let YExtremes;
                                        if (isAssetsVsDebt &&
                                            rawData?.[0].sixmonths?.length &&
                                            rawData?.[1]?.sixmonths?.length) {
                                            YExtremes = getYExtremes(rawData[0].sixmonths, rawData[1].sixmonths, false, false);
                                        }
                                        return {
                                            one: getPointsFromData(rawData?.[0].sixmonths, YExtremes, false, isAssetsVsDebt),
                                            two: rawData?.[1]?.sixmonths
                                                ? getPointsFromData(rawData?.[1].sixmonths, YExtremes, false)
                                                : null,
                                        };
                                    }
                                    return null;
                                }
                                default:
                                    if (rawData?.[0].threemonths?.length) {
                                        let YExtremes;
                                        if (isAssetsVsDebt &&
                                            rawData?.[0].threemonths?.length &&
                                            rawData?.[1]?.threemonths?.length) {
                                            YExtremes = getYExtremes(rawData[0].threemonths, rawData[1].threemonths, false, false);
                                        }
                                        return {
                                            one: getPointsFromData(rawData?.[0].threemonths, YExtremes, false, isAssetsVsDebt),
                                            two: rawData?.[1]?.threemonths
                                                ? getPointsFromData(rawData?.[1].threemonths, YExtremes, false)
                                                : null,
                                        };
                                    }
                                    return null;
                            }
                        })();
                        const dataTwo = data?.two;
                        const dataOneLen = data?.one?.points?.length;
                        const dataTwoLen = data?.two?.points?.length;
                        if (dataOneLen && dataTwo && dataTwoLen && isAssetsVsDebt) {
                            const finalMaxPoint = 
                            // eslint-disable-next-line no-nested-ternary
                            data.one.maxPoint && data.two?.maxPoint
                                ? data.two?.maxPoint.actualY > data.one.maxPoint.actualY
                                    ? data.two?.maxPoint
                                    : data.one.maxPoint
                                : null;
                            const finalMinPoint = 
                            // eslint-disable-next-line no-nested-ternary
                            data.one.minPoint && data.two?.minPoint
                                ? data.one.minPoint.actualY < data.two?.minPoint.actualY
                                    ? data.one.minPoint
                                    : data.two?.minPoint
                                : null;
                            cache.current['Net Worth - Main Asset'][selectedPeriod] = {
                                maxPoint: finalMaxPoint,
                                minPoint: finalMinPoint,
                                points: data.one.points,
                                lastItem: data.one.lastItem,
                                firstItem: data.one.firstItem,
                            };
                            cache.current['Net Worth - Main Debt'][selectedPeriod] = {
                                maxPoint: null,
                                minPoint: null,
                                firstItem: null,
                                points: data.two?.points,
                                lastItem: data.two?.lastItem,
                            };
                            setPortfolioDataSet((oldDataSet) => ({
                                ...(selectedTitle.current !== overviewScreenTitle
                                    ? {
                                        '1W': null,
                                        '1M': null,
                                        '3M': null,
                                        '6M': null,
                                        YTD: null,
                                        '1Y': null,
                                        ALL: null,
                                    }
                                    : oldDataSet),
                                [selectedPeriod]: {
                                    maxPoint: finalMaxPoint,
                                    minPoint: finalMinPoint,
                                    points: data.one.points,
                                    lastItem: data.one.lastItem,
                                    firstItem: data.one.firstItem,
                                },
                            }));
                            setPortfolioDataSet2((oldDataSet) => ({
                                ...(selectedTitle.current !== overviewScreenTitle
                                    ? {
                                        '1W': null,
                                        '1M': null,
                                        '3M': null,
                                        '6M': null,
                                        YTD: null,
                                        '1Y': null,
                                        ALL: null,
                                    }
                                    : oldDataSet),
                                [selectedPeriod]: {
                                    maxPoint: null,
                                    minPoint: null,
                                    firstItem: null,
                                    points: dataTwo.points,
                                    lastItem: dataTwo.lastItem,
                                },
                            }));
                            setRequesting(false);
                        }
                        else if (dataOneLen && !isAssetsVsDebt) {
                            cache.current[overviewScreenTitle][selectedPeriod] = {
                                points: data.one.points,
                                maxPoint: data.one.maxPoint,
                                minPoint: data.one.minPoint,
                                lastItem: data.one.lastItem,
                                firstItem: data.one.firstItem,
                            };
                            setPortfolioDataSet((oldDataSet) => ({
                                ...(selectedTitle.current !== overviewScreenTitle
                                    ? {
                                        '1W': null,
                                        '1M': null,
                                        '3M': null,
                                        '6M': null,
                                        YTD: null,
                                        '1Y': null,
                                        ALL: null,
                                    }
                                    : oldDataSet),
                                [selectedPeriod]: {
                                    points: data.one.points,
                                    maxPoint: data.one.maxPoint,
                                    minPoint: data.one.minPoint,
                                    lastItem: data.one.lastItem,
                                    firstItem: data.one.firstItem,
                                },
                            }));
                            setRequesting(false);
                        }
                        else {
                            setRequesting(true);
                            setPortfolioDataSet((oldDataSet) => ({
                                ...oldDataSet,
                                '1W': null,
                                '1M': null,
                                '3M': null,
                                '6M': null,
                                YTD: null,
                                '1Y': null,
                                ALL: null,
                            }));
                            setPortfolioDataSet2((oldDataSet) => ({
                                ...oldDataSet,
                                '1W': null,
                                '1M': null,
                                '3M': null,
                                '6M': null,
                                YTD: null,
                                '1Y': null,
                                ALL: null,
                            }));
                        }
                    }
                }
            }
            selectedTitle.current = overviewScreenTitle;
        }
    }, [
        cache,
        checkingBalance,
        setPortfolioDataSet,
        selectedPeriod,
        savingsBalance,
        investmentsBalance,
        networthBalance,
        setRequesting,
        chartType,
        overviewScreenTitle,
        debtBalance,
        assetsBalance,
        setPortfolioDataSet2,
        allData,
    ]);
    return {
        pieData,
        chartType,
        requesting,
        setChartType,
        selectedPeriod,
        selectedInfoObj,
        selectedInfoObj2,
        portfolioDataSet,
        portfolioDataSet2,
        setSelectedPeriod,
    };
};
export default useOverviewChart;
